// Factory for a blank wizard draft. Used by useCampaignDraft for /campaigns/new.

import { CAMPAIGN_STATUS, CHANNEL, LIMITS, OFFER } from '../constants';

export const createEmptyDraft = ({ timezone = 'Europe/Stockholm' } = {}) => {
  const now = new Date();
  const defaultUpsellEnd = new Date(now.getTime() + LIMITS.DEFAULT_UPSELL_WINDOW_DAYS * 24 * 60 * 60 * 1000);

  return {
    id: null,
    name: '',
    description: '',
    channels: [CHANNEL.EMAIL],
    offer: OFFER.NONE,
    status: CAMPAIGN_STATUS.DRAFT,
    template: null,
    audience: {
      // Role is implicit per UX doc §7 — the backend always restricts to students.
      // Users add content filters on top of that.
      filters: [],
      // null = unknown / not yet computed. The audience preview query mirrors the
      // resolved count back. Validators treat null as "don't block".
      estimatedReach: null,
      excludeUserIds: [],
    },
    content: {
      variants: [
        {
          id: 'default',
          subject: '',
          previewText: '',
          body: '',
          callToAction: { text: '', url: '' },
          audienceShare: 1.0,
        },
      ],
      networkUpsell: { featuredCourseIds: [] },
    },
    invitation: null,
    discount: null,
    schedule: {
      email: { sendAt: null, timezone, mode: 'scheduled' },
      networkUpsell: {
        activeFrom: null,
        activeUntil: defaultUpsellEnd.toISOString(),
        timezone,
      },
      syncStartTimes: true,
    },
  };
};

// Reset the fields that are no longer meaningful when channels or offer change.
// Called from the destructive-change confirmation dialog.
export const cleanForChannels = (draft, nextChannels) => {
  const next = { ...draft, channels: nextChannels };
  if (!nextChannels.includes(CHANNEL.EMAIL)) {
    next.content = {
      ...draft.content,
      variants: [
        { id: 'default', subject: '', previewText: '', body: '', callToAction: { text: '', url: '' }, audienceShare: 1.0 },
      ],
    };
    if (next.schedule?.email) {
      next.schedule = { ...draft.schedule, email: { sendAt: null, timezone: draft.schedule.email.timezone, mode: 'scheduled' } };
    }
  }
  if (!nextChannels.includes(CHANNEL.NETWORK_UPSELL)) {
    next.content = {
      ...next.content,
      networkUpsell: { featuredCourseIds: [] },
    };
    if (next.schedule?.networkUpsell) {
      next.schedule = {
        ...next.schedule,
        networkUpsell: { ...next.schedule.networkUpsell, activeFrom: null, activeUntil: null },
      };
    }
  }
  return next;
};

export const cleanForOffer = (draft, nextOffer) => {
  const next = { ...draft, offer: nextOffer };
  if (nextOffer !== OFFER.INVITATION) next.invitation = null;
  if (nextOffer !== OFFER.DISCOUNT) next.discount = null;
  if (nextOffer === OFFER.INVITATION && !next.invitation) {
    next.invitation = { courseId: null, expiresAt: null };
  }
  if (nextOffer === OFFER.DISCOUNT && !next.discount) {
    next.discount = { code: '', percentage: 10, validUntil: null, scopeCourseIds: null };
  }
  return next;
};
